/**
 * semrushAds.ts — who pays for Google ads on a keyword, and what one advertiser runs: its ad copy, the keywords it pays
 * for, and the last 12 months of its ads. Pure: reading Semrush's tables into rows.
 *
 * Price per line (Semrush's published prices): advertisers on a keyword 20, ad copy 40, paid keywords 20, 12-month history 100.
 * A lookup asks for `limit` lines, so what it can cost is shown before it runs. A figure Semrush did not give is null.
 */
import { z } from "zod";
import { adProductIdSchema } from "./adsProducts.ts";
import { KEYWORD_MARKETS } from "./semrushKeywords.ts";
import { SEMRUSH_RUN_UNIT_CAP, type SemrushTable } from "./semrush.ts";

export const ADVERTISER_KINDS = ["ads", "keywords", "history"] as const;
export type AdvertiserKind = (typeof ADVERTISER_KINDS)[number];
export const ADVERTISER_KIND_LABEL: Record<AdvertiserKind, string> = { ads: "Their ads", keywords: "Keywords they pay for", history: "Last 12 months" };

/** The Semrush report behind each question. */
export const ADVERTISER_REPORT = { advertisers: "phrase_adwords", ads: "domain_adwords_unique", keywords: "domain_adwords", history: "domain_adwords_historical" } as const;
export const ADVERTISER_UNITS_PER_LINE = { advertisers: 20, ads: 40, keywords: 20, history: 100 } as const;
export const ADVERTISER_LIMITS = { default: 10, max: 30 } as const;

export const advertiserCost = (what: keyof typeof ADVERTISER_UNITS_PER_LINE, limit: number): number => Math.min(ADVERTISER_UNITS_PER_LINE[what] * limit, SEMRUSH_RUN_UNIT_CAP);

const marketSchema = z.enum(KEYWORD_MARKETS);

export const advertisersBodySchema = z.object({
  siteId: z.string().min(1),
  product: adProductIdSchema,
  phrase: z.string().trim().min(2, "Type a keyword first.").max(120).refine((s) => !/[;\n\r]/.test(s), "One keyword or phrase at a time, without semicolons."),
  market: marketSchema.default("us"),
  limit: z.number().int().min(1).max(ADVERTISER_LIMITS.max).default(ADVERTISER_LIMITS.default),
});

/** A website name typed or pasted in any usual form → "example.com", or null when it is not a website name. */
export function cleanDomain(raw: string): string | null {
  const d = raw.trim().toLowerCase().replace(/^[a-z][a-z0-9+.-]*:\/\//, "").replace(/^www\./, "").split(/[/?#]/)[0]!.replace(/:\d+$/, "").replace(/\.$/, "");
  return /^(?=.{4,253}$)([a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,24}$/.test(d) ? d : null;
}

export const domainBodySchema = z.object({
  siteId: z.string().min(1),
  product: adProductIdSchema,
  domain: z.string().transform((s, ctx) => cleanDomain(s) ?? (ctx.addIssue({ code: "custom", message: "That is not a website name, like example.com." }), z.NEVER)),
  kind: z.enum(ADVERTISER_KINDS),
  market: marketSchema.default("us"),
  limit: z.number().int().min(1).max(ADVERTISER_LIMITS.max).default(ADVERTISER_LIMITS.default),
});

export const advertiserRowSchema = z.object({ domain: z.string(), url: z.string().nullable(), visibleUrl: z.string().nullable() });
export const adCopyRowSchema = z.object({ title: z.string(), description: z.string(), visibleUrl: z.string().nullable(), url: z.string().nullable(), keywords: z.number().nullable() });
export const paidKeywordRowSchema = z.object({ keyword: z.string(), position: z.number().nullable(), volume: z.number().nullable(), cpc: z.number().nullable(), url: z.string().nullable() });
export const historyRowSchema = z.object({ keyword: z.string(), month: z.string().nullable(), position: z.number().nullable(), title: z.string(), description: z.string(), visibleUrl: z.string().nullable() });
export type AdvertiserRow = z.infer<typeof advertiserRowSchema>;
export type AdCopyRow = z.infer<typeof adCopyRowSchema>;
export type PaidKeywordRow = z.infer<typeof paidKeywordRowSchema>;
export type HistoryRow = z.infer<typeof historyRowSchema>;

const common = { unitsUsed: z.number(), stoppedEarly: z.boolean(), notes: z.array(z.string()) };
export const advertisersResultSchema = z.object({ phrase: z.string(), market: marketSchema, rows: z.array(advertiserRowSchema), ...common });
export const domainResultSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("ads"), domain: z.string(), market: marketSchema, rows: z.array(adCopyRowSchema), ...common }),
  z.object({ kind: z.literal("keywords"), domain: z.string(), market: marketSchema, rows: z.array(paidKeywordRowSchema), ...common }),
  z.object({ kind: z.literal("history"), domain: z.string(), market: marketSchema, rows: z.array(historyRowSchema), ...common }),
]);
export type AdvertisersResult = z.infer<typeof advertisersResultSchema>;
export type DomainResult = z.infer<typeof domainResultSchema>;

const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, "");
function cell(row: Record<string, string>, names: string[]): string | null {
  const keys = Object.keys(row);
  for (const n of names) {
    const hit = keys.find((k) => norm(k) === norm(n));
    if (hit !== undefined) return row[hit] ?? null;
  }
  for (const n of names) {
    const hit = keys.find((k) => norm(k).includes(norm(n)));
    if (hit !== undefined) return row[hit] ?? null;
  }
  return null;
}
const text = (v: string | null) => (v ?? "").trim();
const textOrNull = (v: string | null) => (text(v) === "" ? null : text(v));
function num(v: string | null): number | null {
  const t = text(v).replace(/,/g, "");
  return /^-?\d+(\.\d+)?$/.test(t) ? Number(t) : null;
}

/** "20261015" → "2026-10" (Semrush gives the date as YYYYMMDD). Null when it is not one. */
export function historyMonth(v: string | null): string | null {
  const m = /^(\d{4})(\d{2})\d{2}$/.exec(text(v));
  return m ? `${m[1]}-${m[2]}` : null;
}

export function advertisersOf(t: SemrushTable): AdvertiserRow[] {
  return t.rows.flatMap((r) => {
    const domain = cleanDomain(text(cell(r, ["Domain", "Dn"])));
    return domain ? [{ domain, url: textOrNull(cell(r, ["Url", "Ur"])), visibleUrl: textOrNull(cell(r, ["Visible Url", "Vu"])) }] : [];
  });
}
export function adCopiesOf(t: SemrushTable): AdCopyRow[] {
  return t.rows.flatMap((r) => {
    const title = text(cell(r, ["Title", "Tt"]));
    const description = text(cell(r, ["Description", "Ds"]));
    return title || description ? [{ title, description, visibleUrl: textOrNull(cell(r, ["Visible Url", "Vu"])), url: textOrNull(cell(r, ["Url", "Ur"])), keywords: num(cell(r, ["Number of Keywords", "Pc"])) }] : [];
  });
}
export function paidKeywordsOf(t: SemrushTable): PaidKeywordRow[] {
  return t.rows.flatMap((r) => {
    const keyword = text(cell(r, ["Keyword", "Ph"]));
    return keyword ? [{ keyword, position: num(cell(r, ["Position", "Po"])), volume: num(cell(r, ["Search Volume", "Nq"])), cpc: num(cell(r, ["CPC", "Cp"])), url: textOrNull(cell(r, ["Url", "Ur"])) }] : [];
  });
}
export function historyOf(t: SemrushTable): HistoryRow[] {
  return t.rows.flatMap((r) => {
    const keyword = text(cell(r, ["Keyword", "Ph"]));
    return keyword ? [{ keyword, month: historyMonth(cell(r, ["Date", "Dt"])), position: num(cell(r, ["Position", "Po"])), title: text(cell(r, ["Title", "Tt"])), description: text(cell(r, ["Description", "Ds"])), visibleUrl: textOrNull(cell(r, ["Visible Url", "Vu"])) }] : [];
  });
}
